import {Injectable} from '@angular/core';
import {RuntimeService} from './runtime.service';

@Injectable({
    providedIn: 'root'
})
export class MobileOptimizationService {
    constructor(private runtimeService: RuntimeService) {
        this.detectDevice();
        this.detectConnection();
        this.detectHardware();
    }

    private _isMobile: boolean = false;

    /**
     * Returns true if the current device is a mobile device
     */
    get isMobile(): boolean {
        return this._isMobile;
    }

    private _isTablet: boolean = false;

    /**
     * Returns true if the current device is a tablet
     */
    get isTablet(): boolean {
        return this._isTablet;
    }

    private _isLowEndDevice: boolean = false;

    /**
     * Returns true if the current device is a low-end device
     */
    get isLowEndDevice(): boolean {
        return this._isLowEndDevice;
    }

    private _connectionType: string = 'unknown';

    /**
     * Returns the connection type
     */
    get connectionType(): string {
        return this._connectionType;
    }

    private _effectiveConnectionType: string = 'unknown';

    /**
     * Returns the effective connection type
     */
    get effectiveConnectionType(): string {
        return this._effectiveConnectionType;
    }

    /**
     * Returns true if the current connection is slow
     */
    get isSlowConnection(): boolean {
        return this._effectiveConnectionType === '2g' ||
            this._effectiveConnectionType === 'slow-2g';
    }

    /**
     * Returns true if the current device should use low-quality images
     */
    get shouldUseLowQualityImages(): boolean {
        return this.isLowEndDevice || this.isSlowConnection;
    }

    /**
     * Returns the appropriate image quality based on device and connection
     */
    getImageQuality(): number {
        if (this.isSlowConnection) {
            return 65; // Slightly higher quality for slow connections
        }

        if (this.isLowEndDevice) {
            return 75; // Higher quality for low-end devices
        }

        if (this.isMobile) {
            return 85; // Better quality for mobile devices
        }

        return 90; // High quality for desktop
    }

    /**
     * Returns the appropriate image width based on device
     */
    getImageWidth(defaultWidth: number): number {
        if (this.isMobile) {
            return Math.min(defaultWidth, window.innerWidth - 32); // Account for padding
        }

        if (this.isTablet) {
            return Math.min(defaultWidth, window.innerWidth - 64); // Account for padding
        }

        return defaultWidth;
    }

    /**
     * Detects if the current device is a mobile or tablet
     */
    private detectDevice(): void {
        if (!this.runtimeService.isBrowser) {
            return;
        }

        // Check if mobile
        this._isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) &&
            window.innerWidth < 768;

        // Check if tablet
        this._isTablet = /iPad|Android/i.test(navigator.userAgent) &&
            window.innerWidth >= 768 &&
            window.innerWidth < 1024;

        // Listen for resize events to update device type
        window.addEventListener('resize', () => {
            this._isMobile = window.innerWidth < 768;
            this._isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;
        });
    }

    /**
     * Detects the network connection type
     */
    private detectConnection(): void {
        if (!this.runtimeService.isBrowser) {
            return;
        }

        // Check if the Network Information API is available
        const connection = (navigator as any).connection ||
            (navigator as any).mozConnection ||
            (navigator as any).webkitConnection;

        if (connection) {
            this._connectionType = connection.type || 'unknown';
            this._effectiveConnectionType = connection.effectiveType || 'unknown';

            // Listen for connection changes
            connection.addEventListener('change', () => {
                this._connectionType = connection.type || 'unknown';
                this._effectiveConnectionType = connection.effectiveType || 'unknown';
            });
        }
    }

    /**
     * Detects if the device is a low-end device
     */
    private detectHardware(): void {
        if (!this.runtimeService.isBrowser) {
            return;
        }

        // Check if device is low-end based on memory
        if ('deviceMemory' in navigator) {
            this._isLowEndDevice = (navigator as any).deviceMemory < 4;
        }

        // Check if device is low-end based on hardware concurrency
        if ('hardwareConcurrency' in navigator) {
            this._isLowEndDevice = this._isLowEndDevice || navigator.hardwareConcurrency < 4;
        }
    }
}
